import { Fragment } from "react";
import { Link } from "react-router-dom";
import { SCHEDULE } from "../lib/schedule";

export function ScheduleSection() {
  return (
    <>
      <ScheduleIntro />
      <ScheduleTimeline />
    </>
  );
}

/**
 * Cream band that eases the teal hero into the dark schedule. Pairs a short
 * note with an engagement photo. Copy and image are placeholders for now.
 */
function ScheduleIntro() {
  return (
    <section className="bg-cream px-6 py-14 text-pine md:py-20">
      <div className="mx-auto grid max-w-5xl items-center gap-10 md:grid-cols-2 md:gap-16">
        {/* Engagement photo (#32) */}
        <div className="relative mx-auto aspect-[4/5] w-full max-w-sm overflow-hidden rounded-sm border border-pine/15 bg-dusty-teal/20 shadow-[0_24px_70px_-18px_rgba(10,51,35,0.45)]">
          <img
            src="/engagement/sp-32.webp"
            alt="Karishma and Ben"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        {/* Note */}
        <div className="text-center md:text-left">
          {/* <p className="text-sm uppercase tracking-[0.4em] text-midnight-teal">
            woowoowoowoowoowoo
          </p> */}
          <p className="mt-5 font-serif text-3xl leading-snug text-pine md:text-4xl">
            We can't wait to celebrate with you!
          </p>
          <p className="mt-5 max-w-xl font-sans text-lg font-normal tracking-[0.02em] text-pine">
            Please see below for a tentative scedule of the weekend, and{" "}
            <Link
              to="/rsvp"
              className="underline decoration-pine/40 underline-offset-4 transition-colors hover:text-midnight-teal hover:decoration-midnight-teal"
            >
              kindly RSVP
            </Link>{" "}
            by October 30th to let us know if you'll be able to attend.
          </p>
          <div
            aria-hidden="true"
            className="mx-auto mt-8 flex w-32 items-center gap-3 md:mx-0"
          >
            <span className="h-px flex-1 bg-pine/20" />
            <span className="inline-block h-1.5 w-1.5 rotate-45 bg-sage-moss" />
            <span className="h-px flex-1 bg-pine/20" />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Renders a day label, superscripting ordinal suffixes (30th → 30ᵗʰ). */
function DayLabel({ label }: { label: string }) {
  const parts = label.split(/(\d+(?:st|nd|rd|th)|❃)/);
  return (
    <>
      {parts.map((part, i) => {
        if (part === "❃") {
          // The site's diamond accent between the day and the date. Sized in
          // `em` so it tracks the heading across breakpoints, and `align-middle`
          // sits it on the text's midline instead of the baseline.
          return (
            <span
              key={i}
              aria-hidden="true"
              className="mx-[0.55em] inline-block h-[0.3em] w-[0.3em] rotate-45 bg-dusty-rose align-middle"
            />
          );
        }
        const match = /^(\d+)(st|nd|rd|th)$/.exec(part);
        if (!match) return <Fragment key={i}>{part}</Fragment>;
        return (
          <Fragment key={i}>
            {match[1]}
            <sup className="text-[0.55em] tracking-normal">{match[2]}</sup>
          </Fragment>
        );
      })}
    </>
  );
}

/**
 * Timeline marker: the event's line-art icon straddling the rail, centered on
 * the event block, or the plain dot if an event has no icon yet.
 *
 * The icon is painted as a mask (see `.mask-icon`) so it takes the cream from
 * `bg-cream/85`. The pine backdrop is what breaks the rail line behind it — a
 * mask is transparent between strokes, so the line would otherwise show
 * through the artwork.
 */
function EventMarker({ icon }: { icon?: string }) {
  if (!icon) {
    return (
      <span
        aria-hidden="true"
        className="absolute -left-12 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-dusty-rose sm:-left-20"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="absolute -left-12 top-1/2 flex -translate-x-1/2 -translate-y-1/2 bg-pine py-1.5 sm:-left-20"
    >
      <span
        style={{ maskImage: `url(${icon})`, WebkitMaskImage: `url(${icon})` }}
        className="mask-icon h-12 w-12 bg-cream/85 sm:h-16 sm:w-16"
      />
    </span>
  );
}

/** Static, grouped-by-day timeline on the dark pine feature background. */
function ScheduleTimeline() {
  return (
    <section
      aria-label="Wedding weekend schedule"
      className="bg-pine px-6 pb-24 pt-14 text-cream md:pb-32 md:pt-20"
    >
      <div className="mx-auto max-w-3xl">
        <div className="space-y-16">
          {SCHEDULE.map((day) => (
            <div key={day.label}>
              {/* The flanking rules only work when the label fits on one
                  line. On mobile it wraps, leaving stub lines colliding with
                  the text — so drop them there and let the label center on
                  its own. */}
              <div className="mb-10 flex items-center justify-center gap-6">
                <span
                  aria-hidden="true"
                  className="hidden h-px flex-1 bg-cream/20 sm:block"
                />
                <h3 className="text-center font-serif text-xl font-bold uppercase tracking-[0.12em] text-cream lining-nums sm:text-2xl">
                  <DayLabel label={day.label} />
                </h3>
                <span
                  aria-hidden="true"
                  className="hidden h-px flex-1 bg-cream/20 sm:block"
                />
              </div>

              <ol className="relative ml-6 space-y-11 border-l border-cream/15 pl-12 sm:ml-0 sm:pl-20">
                {day.events.map((event) => (
                  <li key={event.title} className="relative">
                    <EventMarker icon={event.icon} />
                    <p className="font-serif text-2xl font-bold uppercase tracking-[0.08em] text-dusty-rose lining-nums tabular-nums">
                      {event.time}
                    </p>
                    <h4 className="mt-1.5 mb-3 font-serif text-4xl leading-tight text-cream">
                      {event.title}
                    </h4>
                    {event.attire && event.attireId && (
                      <Link
                        to={`/schedule#${event.attireId}`}
                        className="inline-block font-serif text-base uppercase tracking-[0.08em] text-cream/75 underline decoration-cream/25 underline-offset-4 transition-colors hover:text-cream hover:decoration-cream/60"
                      >
                        {event.attire}
                      </Link>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
